import { NO_SOUND_THRESHOLD_DISTANCE } from '@/utils/constants';
import { DataService } from '@/service/DataService';
import { JitsiController, JitsiParticipant, TrackAndElement, UserParticipant } from '@/service/JitsiLibWrapper';
import { RoomConnectionManager } from '@/service/RoomConnectionManager';
import { EventEmitter } from 'eventemitter3';
import { SCREEN_SHARE_IDLE_TIMEOUT } from '@/utils/constants';
import 'iscroll';
export interface ZawaroomStatus {
  iscroll: IScroll;
  // jitsi
  jitsi?: RoomConnectionManager;
  dataService: DataService | null;
}

export const zawaroom: ZawaroomStatus = { iscroll: <any>{}, dataService: null };
// debug 用
(window as any).zawaroom = zawaroom;

export interface SiteInfo {
  /**
   * guid
   */
  id: string;
  /**
   * このサイトのURL.
   * 特に使っていないけど分かりやすいように設定
   */
  siteUri: string;
  /**
   * このサイトが利用するメディアサーバ―のURL。
   * 基本、siteUri と同じがいいかな。
   * cors は許可されているのでパスワードさえ合えば問題ない
   */
  defaultMediaServerUrl: string;
  /**
   * 認証プロバイダーの設定。
   * guest/aad/aad-zawa/aad-b2c/password/b2c-demo が指定可能。
   *
   * 今後増えていく予定
   */
  authLoginProviders: string[];
  /**
   * 許可するドメイン.
   * emailと比較。
   * 後方一致
   */
  allowDomainNames: string[];
  /**
   * member の自動登録の許可設定。
   *
   * auto: allowDomainNames のドメインは自動登録。 autoRegisterDomainNames が指定されていない場合はすべて許可(件数も無視)
   * semi-auto: allowDomainNames のみ自動で登録。 autoRegisterDomainNames が指定されていない場合は不許可。上限に達した場合はエラー
   * manual: 自動塘路kはしない。
   */
  userRegisterType: 'auto' | 'semi-auto' | 'manual';
  /**
   * auto/semi-auto 時の自動登録許可ドメイン設定。
   *
   * auto: autoRegisterDomainNames が指定されていない場合はすべて許可(件数も無視)
   * semi-auto: autoRegisterDomainNames が指定されていない場合は不許可。上限に達した場合はエラー
   */
  autoRegisterDomainNames: string[];
  /**
   * 登録可能な最大メンバー数。
   * 1以下の場合は無制限。
   */
  maxUsersCount: number;
  /**
   * 設定したURL（サポートサイト等）へのリンクボタンを表示するか否かの設定。
   */
  showSiteLink: 'yes' | 'no';
  /**
   * 表示するサイトの名称の設定。
   */
  siteLinkName: string;
  /**
   * 表示するサイトのURLの設定。
   */
  siteLinkUrl: string;
  /**
   * トークンのタイムアウト時間(分)
   */
  tokenTimeoutMinute?: number;
  /**
   * デモ用のサイトかどうか
   */
  isDemo: boolean;
  /**
   * サイト停止中かどうか
   */
  suspended: boolean;
  /**
   * TMC向けのお知らせを表示するかどうか
   */
  showTMCAnnouncement: boolean;
}

/**
 * サイトの公開状況
 */
export interface SiteStatus {
  /**
   * zawa prefix - 通常zawaroom
   *
   * zawademo prefix - デモ版zawaroom
   */
  status:
    | 'zawa-operational'
    | 'zawa-suspended'
    | 'zawademo-operational'
    | 'zawademo-suspended'
    | 'zawademo-scheduled-suspension';
}
export interface SiteMember {
  authType: string;
  email: string;
  userId?: string;
  isSitesAdmin: boolean;
  autoDelete?: boolean;
}
export interface RemoteTrackModel {
  remoteTrackInfo: RemoteTrackInfo;
  type: string;
  track: any;
}

export interface RemoteTrackInfo {
  id: string;
  participantId: string;
}

export interface ParticipantTracks {
  participantId: string;
  videoTrack: RemoteTrackInfo | null;
  audioTrack: RemoteTrackInfo | null;
}
export type UserRoomStateDataEvents = 'tracksUpdate' | 'audioLevelChanged' | 'participantChanged';
export class UserRoomStateData {
  mutedLocally: boolean = false;
  subRoomId: string = '';
  roomVolume: number = -1;
  distance: number = 0;
  distanceSensitiveVolume: number = 0;
  modeVolume: number = 0;
  scheduleInfo: ScheduleInfo | null = null;
  isSameSubRoom: boolean = false;

  private userParticipant_: UserParticipant | null = null;
  private readonly event_ = new EventEmitter<UserRoomStateDataEvents, any>();
  get userParticipant(): UserParticipant | null {
    return this.userParticipant_;
  }
  set userParticipant(val: UserParticipant | null) {
    if (this.userParticipant_ === val) {
      return;
    }
    if (this.userParticipant_) {
      this.userParticipant_.off('tracksUpdate', this.onTrackUpdate);
      this.userParticipant_ = null;
    }
    if (val) {
      val.on('tracksUpdate', this.onTrackUpdate);
      this.userParticipant_ = val;
    }
    this.event_.emit('participantChanged', val);
  }

  constructor(public userState: UserRoomState) {}
  private onTrackUpdate = (ev?: TrackAndElement | number | JitsiParticipant) => {
    this.event_.emit('tracksUpdate', ev);

    if (ev && typeof ev !== 'number' && (ev as TrackAndElement).track?.getType() === 'audio') {
      this.applyVolume();
    }
  };
  onAudioLevelChanged = (ev?: TrackAndElement | number | JitsiParticipant) => {
    this.event_.emit('audioLevelChanged', ev);
  };
  on(eventName: UserRoomStateDataEvents, callback: (ev?: TrackAndElement | number | JitsiParticipant) => void) {
    this.event_.on(eventName, callback);
  }
  off(eventName: UserRoomStateDataEvents, callback: (ev?: TrackAndElement | number | JitsiParticipant) => void) {
    this.event_.off(eventName, callback);
  }
  updateVolume(position: CoordinatesPosition, subRoomId: string, roomVolume: number, volumeMode: VolumeModes) {
    this.roomVolume = roomVolume;
    this.isSameSubRoom = this.subRoomId === subRoomId;
    this.distance = this.calculateDistance(position);
    this.distanceSensitiveVolume = this.calculateDistanceVolume(this.distance);
    this.modeVolume = this.calcModeVolume(roomVolume, volumeMode);
    this.applyVolume();
  }
  calculateDistance(pos: CoordinatesPosition) {
    return Math.round(
      Math.sqrt(Math.pow(this.userState.position.x - pos.x, 2) + Math.pow(this.userState.position.y - pos.y, 2)),
    );
  }
  private calculateDistanceVolume(distance: number) {
    const threshold = this.userState.voiceArea ?? NO_SOUND_THRESHOLD_DISTANCE;
    const maxThreshold = Math.max(150, Math.ceil(threshold / 10));
    if (distance > threshold) {
      return 0;
    } else if (distance < maxThreshold) {
      return 1;
    } else {
      const remain = distance - maxThreshold;
      const remainThreshold = threshold - maxThreshold;
      const vol = 1 - Math.ceil(Math.pow(remain / remainThreshold, 2) * 1000) / 1000;
      return Math.max(0, vol);
    }
  }
  private calcModeVolume(roomVolume: number, volumeMode: VolumeModes) {
    if (!this.isSameSubRoom) {
      return 0;
    }
    const normalizedRoomVolume = typeof roomVolume === 'number' && roomVolume >= 0 ? roomVolume : undefined;
    switch (volumeMode) {
      case 'Muted':
        return 0;
      case 'MaxVolume':
        return 1;
      case 'DistanceSensitive':
      default:
        return normalizedRoomVolume ?? this.distanceSensitiveVolume;
    }
  }
  applyVolume() {
    if (!this.userParticipant) {
      return;
    }
    const ts = this.userParticipant.audioTracks;
    ts.forEach((t) => {
      const volume = this.mutedLocally ? 0 : this.modeVolume;
      t.el.volume = volume;
      if (volume <= 0) {
        t.el.pause();
      } else if (t.el.paused) {
        t.el.play();
      }
    });
  }
}
export interface ActivityLog {
  id: string;
  eventType: string;
  userId: string;
  sid?: string;
  spaceId: string;
  space: string;
  roomId: string;
  room: string;
  target: string;
  note?: string;
  eventDateTime: number;
}

export type RoomActivityLog = Pick<ActivityLog, 'id' | 'eventType' | 'userId' | 'eventDateTime' | 'note' | 'target'>;
export type RoomActivityLogWithUser = {
  log: RoomActivityLog;
  user: User;
  targetDisplayName: string | undefined;
};
export type RoomActivityLogs = {
  users: User[];
  logs: RoomActivityLog[];
};
export class RoomActivitiesManager {
  readonly logs: RoomActivityLogWithUser[] = [];
  private readonly logIdSet = new Set<string>();
  private readonly userIdMap = new Map<string, User>();
  constructor() {}
  addLogs(logs: RoomActivityLogs) {
    logs.users.forEach((u) => this.userIdMap.set(u.id, u));
    logs.logs.forEach((log) => {
      if (this.logIdSet.has(log.id)) {
        return;
      }
      this.logIdSet.add(log.id);
      const user = this.userIdMap.get(log.userId);
      if (!user) {
        console.log('履歴に不明なユーザーIDがありました。', log);
        return;
      }
      const targetUser = this.userIdMap.get(log.target);
      const targetDisplayName = targetUser ? targetUser.displayName || targetUser.name : undefined;
      this.logs.push({ log, user, targetDisplayName });
    });
    this.logs.sort((a, b) => a.log.eventDateTime - b.log.eventDateTime);
  }
}
export interface BoundingPosition extends CoordinatesPosition {
  width: number;
  height: number;
}

export interface HTMLElementWithPosition {
  position: CoordinatesPosition;
  width: number;
  heigh: number;
  htmlElementRef: HTMLDivElement | HTMLMediaElement;
  parentElement?: HTMLDivElement;
}

export interface CoordinatesPosition {
  x: number;
  y: number;
}
export interface StatusItem {
  id: string;
  type: string;
  name: string;
  selectable: boolean;
}
export interface UserOnServer {
  userId: string;
  idInConference: string;
  name: string;
  lastLogin: number;
  position: CoordinatesPosition;
  userMessage: string;
  handRaised: boolean;
  audioMuted: boolean;
  videoMuted: boolean;
  status: string;
  isFocus: boolean;
  avatarBase64: string;
}

export interface DrawMessage {
  type: string;
  coordinates: { start: CoordinatesPosition; end: CoordinatesPosition };
  strokeColor: string;
  strokeWeight: number;
}

export interface EraseMessage {
  type: string;
  coordinates: CoordinatesPosition;
  rubberSize: number;
}

export type UploadImageType = 'upload image' | 'upload avatar';

export interface SubRoom {
  /** 小ルームID.  url のパスになる文字列 */
  subid: string;
  /** 小ルームの名称。 */
  name?: string;
  /** 小ルームの名称表示設定。設定してあると名前名称を表示可能 */
  showName: boolean;
  /** 名前の表示するときの div のスタイル. 位置情報を必ず含めてください。top:xx, left:xx */
  nameStyle?: string;
  /** エリア表示時のスタイル。色などを指定してください。 */
  areaStyle?: string;
  /** 左上の場所 */
  topLeft: CoordinatesPosition;
  /** 右下の場所 */
  bottomRight: CoordinatesPosition;
  /** 小ルーム内の音量。固定にする場合は指定してください。 負の値を指定された場合は無視されます。*/
  volume?: number;
  /** BGM を流す場合は、そのURLを指定してください。。*/
  bgmUrl?: string;
  /** GBM URL が設定されている場合、その音量を指定してください。未指定の場合再生しません。 */
  bgmAudioVolume?: number;

  // ローカルのみ
  userStates: UserRoomStateData[];
}
export interface UserRoomState {
  /** User クラスの id. _id ではない。 */
  userId: string;
  /** spaceid _id ではない  */
  spaceId: string;
  /** roomid _id ではない  */
  roomId: string;
  /** 接続中のSocket IO socketId。 */
  socketId: string;
  /** room でのポジション。 */
  position: CoordinatesPosition;
  /** 表示中のメモ */
  userMessage: string;
  /** 手を挙げているかどうか */
  handRaised: boolean;
  /** フォーカスがあるかどうか */
  isFocus: boolean;
  /** 声の聞こえる範囲 */
  voiceArea?: number;
  /**
   * ステータス。
   * Available, AvailableIdle, Away, BeRightBack, Busy, BusyIdle, DoNotDisturb, Offline, PresenceUnknown
   *
   * https://docs.microsoft.com/en-us/graph/api/resources/presence?view=graph-rest-beta
   *
   */
  status: string;
  videoMuted: boolean;
  audioMuted: boolean;
  speakerMuted: boolean;
  user: User;
  /** ゲストの場合そのルームで承認されているか。 */
  allowed: boolean;
}
export type UpdateableUserRoomState = Pick<
  UserRoomState,
  | 'position'
  | 'userMessage'
  | 'handRaised'
  | 'isFocus'
  | 'voiceArea'
  | 'status'
  | 'videoMuted'
  | 'audioMuted'
  | 'speakerMuted'
>;

export interface RoomObject {
  id: string;
  roomType: 'room' | 'subroom' | 'all';
  roomId: string;
  objectType: 'image' | 'share screen' | 'whiteboard' | 'board';
  position: CoordinatesPosition;
  width: number;
  height: number;
  size: number;
  imageBase64: string;
  isLocked: boolean;
  props: { [key: string]: string };
  createdUserId: string;
  createdUserName: string;
  updatedUserId: string;
}
/**
 * ルーム情報
 */
export type GuestPermission = 'allow' | 'approve' | 'deny';
export type ScaleMode = 'fitWidth' | 'fitHeight' | 'custom';
export interface Room {
  /** ルームID. url のパスになる文字列。 */
  id: string;
  /** ルームの名前 ex) UX事業部 */
  name: string;
  /** 部屋の説明 */
  description?: string;
  /** ルームの画像URL */
  roomMapUrl: string;
  /** ルーム画像のサイズ */
  roomSize: { width: number; height: number };
  /** 小ルーム情報 */
  subRooms: SubRoom[];
  /** 初期表示時のポジション */
  entrancePosition: CoordinatesPosition;
  /** 初期表示の際の Scaleモード */
  initialScaleMode: ScaleMode;
  /** 初期表示の際の Scale */
  initialScale?: number;
  /** 最小値モード zoom したときの Scale */
  minScaleMode: ScaleMode;
  /** 最小値 zoom したときの Scale */
  minScale?: number;
  /** 最大限 zoom したときの Scale */
  maxScale?: number;
  /** ステータス一覧 */
  roomStates: UserRoomState[];
  /** 部屋のデフォルト範囲 */
  defaultVoiceArea?: number;
  /** ゲストの権限設定 */
  guestPermission: GuestPermission;
  /** 登録ユーザーすべてに公開するかどうか */
  isPublicRoom?: boolean;
  /** ルームに入室許可されているドメイン */
  allowDomains?: string[];
  /** ルームに入室許可されているメールアドレス */
  allowUserEmails?: string[];
  /** 管理者となる人のemail */
  adminEmails?: string[];
  /** 入室時のベル音量 */
  enteringSoundVolume?: number;
  /**　会議室入室時のベル音量 */
  doorSoundVolume?: number;

  /** スペースID クライアントに返す時にだけ設定 */
  spaceId: string;
  /** スペース名 クライアントに返す時にだけ設定 */
  spaceName: string;

  /** 画面共有範囲。
   *
   * room: 自分がいる部屋にいる人だけに共有
   * voice area: 会話円にいる人だけに共有
   */
  screenShareScope?: 'room' | 'voice area';
  /** 画面共有を自動停止するかどうか。 */
  enableScreenShareIdling?: boolean;
}

export interface RoomSpace {
  /** 空間情報ID. url のパスになる文字列。*/
  id: string;
  /** 空間情報名. ex 南国スペース */
  name: string;
  /** ゲストに利用可能かどうか */
  canGuest: boolean;
  /** スペースの種類。未設定の場合は space とする
   *
   * space: 通常のスペース
   * trial-specific-use: トライアル版zawaroom向けの、特定利用スペース
   * trash: ゴミ箱
   */
  spaceType?: 'space' | 'trial-specific-use' | 'trash';
  /**
   * このスペースを表示可能な組織のメールアドレスのドメイン一覧。
   *
   * @を付けると完全一致。つけないと後方一致.
   * ex. @7659sw.com
   *      nangokrstudios.com
   */
  organizations: string[];
  /** ルーム情報 */
  rooms: Room[];
}

export const TrashBoxSpaceId = 'trashbox';

export class DesktopRoomObject implements RoomObject {
  id: string;
  roomType: 'room' | 'subroom' | 'all';
  roomId: string;
  objectType: 'image' | 'share screen' | 'whiteboard' | 'board';
  position: CoordinatesPosition;
  width: number;
  height: number;
  size: number;
  imageBase64: string;
  isLocked: boolean;
  props: { [key: string]: string };
  createdUserId: string;
  createdUserName: string;
  updatedUserId: string;
  createdUserParticipant: UserParticipant | null;
  createdUserRoomStateData: UserRoomStateData;

  private _visible: boolean = true;
  /**
   *　表示するかどうか
   */
  get visible() {
    return this._visible;
  }
  private _hidden: boolean;
  /**
   * すりガラスなどで隠された表示かどうか
   */
  get hidden() {
    return this._hidden;
  }
  private _isIdle = false;
  /**
   * 自動停止されたかどうか
   */
  get isIdle() {
    return this._isIdle;
  }

  private idleTimerId: number | null = null;
  private enableScreenShareIdling: boolean;
  private screenShareScope: NonNullable<Room['screenShareScope']>;
  private _controller?: JitsiController;
  get controller() {
    return this._controller;
  }
  /** イベント管理 */
  private eventEmitter = new EventEmitter<'idle'>();
  constructor(
    roomObject: RoomObject,
    createdUserPaticipant: UserParticipant,
    createdUserRoomStateData: UserRoomStateData,
    screenShareScope: NonNullable<Room['screenShareScope']>,
    enableScreenShareIdling: boolean,
    hidden: boolean = true,
    controller: JitsiController,
  ) {
    this.id = roomObject.id;
    this.roomType = roomObject.roomType;
    this.roomId = roomObject.roomId;
    this.objectType = roomObject.objectType;
    this.position = roomObject.position;
    this.width = roomObject.width;
    this.height = roomObject.height;
    this.size = roomObject.size;
    this.imageBase64 = roomObject.imageBase64;
    this.isLocked = roomObject.isLocked;
    this.props = roomObject.props;
    this.createdUserId = roomObject.createdUserId;
    this.createdUserName = roomObject.createdUserName;
    this.updatedUserId = roomObject.updatedUserId;
    this.createdUserParticipant = createdUserPaticipant;
    this.createdUserRoomStateData = createdUserRoomStateData;
    this.screenShareScope = screenShareScope;
    this.enableScreenShareIdling = enableScreenShareIdling;
    this._hidden = hidden;
    this._controller = controller;
  }

  /**
   * 画面共有を隠すかどうかと表示の有無を更新します。
   *
   * @param shareScope
   * @param myUserRoomState
   * @param currentSubRoomId
   * @param usersData
   * @param roomVolume
   */
  updateVisualStates(myUserRoomState: UserRoomState, roomVolume?: number): void {
    if (this.screenShareScope === 'room') {
      this.updateVisualStatesWithRoomScope(myUserRoomState);
    } else if (this.screenShareScope === 'voice area') {
      this.updateVisualStatesWithVoiceArea(myUserRoomState, roomVolume);
    }
  }

  /**
   * 自動停止タイマーの開始。
   * @returns
   */
  private startIdleTimer(): void {
    if (this.isIdleTimerWorking || !this.enableScreenShareIdling) {
      return;
    }
    this._isIdle = false;
    this.idleTimerId = window.setTimeout(this.emitIdleEvent.bind(this), SCREEN_SHARE_IDLE_TIMEOUT);
  }

  /**
   * 自動停止タイマーをキャンセル、または画面共有の表示の再開を行います。
   *
   * 例）
   * 自動停止タイマー作動中に誰かが近づいてきた場合→ 停止タイマーをキャンセル
   *
   * 画面共有停止後、誰かが近づいてきた場合→ 画面共有の表示を再開
   *
   * @returns willResume 画面共有の再開する必要がある
   */
  private cancelIdleTimerOrResumeScreen(): boolean {
    if (!this.enableScreenShareIdling) return false;
    let willResume = false;
    if (this.isIdle) {
      //停止状態から再開
      this._isIdle = false;
      this._hidden = false;
      this._visible = true;
      willResume = true;
    } else if (this.isIdleTimerWorking) {
      //タイマーが動いている場合はキャンセル
      window.clearTimeout(this.idleTimerId!);
      this.idleTimerId = null;
    }
    return willResume;
  }

  get isIdleTimerWorking() {
    return !!this.idleTimerId;
  }

  /**
   * 画面共有のIdle Timer制御を行います。
   *
   * 画面共有のIdle状態後、画面共有の再開後にonResumeを実行します。（解像度のアップデートなどの処理を想定しています。）
   * @param myUserRoomState
   * @param roomVolume
   * @param hasViwer
   * @param onResume
   * @returns
   */
  public async updateIdleTimer(
    myUserRoomState: UserRoomState,
    roomVolume: number = 0,
    hasViwer: boolean,
    onResume?: () => any,
  ) {
    if (!this.enableScreenShareIdling) return false;
    if (this.createdUserId !== myUserRoomState.userId) return; //自分の画面共有じゃない場合はスキップ
    if (hasViwer) {
      const willResumeScreen = this.cancelIdleTimerOrResumeScreen();
      //画面共有の再開を行う。（Idle状態からの復帰）
      willResumeScreen && this.updateVisualStates(myUserRoomState, roomVolume);
      willResumeScreen && onResume && onResume();
    } else {
      this.startIdleTimer();
    }
  }

  /**
   * 自動停止時のコールバックを登録します。（実際の画面共有の削除はコールバック内で行うことが期待されています）
   * @param callback
   * @returns
   */
  registerIdleEventHandler(callback: (ss: DesktopRoomObject) => void): void {
    if (!this.enableScreenShareIdling) {
      return;
    }
    this.eventEmitter.on('idle', callback);
  }

  removeAllListeners(): void {
    this.eventEmitter.removeAllListeners();
  }

  private emitIdleEvent(): void {
    this._isIdle = true;
    this.idleTimerId = null;
    this.eventEmitter.emit('idle', this);
  }

  private updateVisualStatesWithRoomScope(myUserRoomState: UserRoomState): void {
    const isMine = this.createdUserId === myUserRoomState.userId;
    this._visible = true;
    if (isMine) {
      this._hidden = this.enableScreenShareIdling && this._isIdle;
    } else {
      this._hidden = false;
    }
  }

  private updateVisualStatesWithVoiceArea(myUserRoomState: UserRoomState, roomVolume?: number): void {
    const isVolumeSet = typeof roomVolume === 'number' && roomVolume === 1; //サブルームによってはルーム内で全員の声が聞こえる設定(volume=1)になっている。
    const sharerPosition = this.createdUserRoomStateData.userState.position;
    const sharerVoiceArea = this.createdUserRoomStateData.userState.voiceArea;
    const isMine = this.createdUserId === myUserRoomState.userId;
    if (isMine) {
      //自分で作った画面共有の更新
      this._visible = true;
      this._hidden = this.enableScreenShareIdling ? this._isIdle : false;
    } else {
      //他のユーザーが作った画面共有の更新
      if (isVolumeSet) {
        this._hidden = false;
        this._visible = true;
        return;
      }
      const userPosition = myUserRoomState.position;
      this._hidden = !this.isInVoiceArea(sharerPosition, userPosition, sharerVoiceArea);
      this._visible = !this._hidden;
    }
  }

  private calculateDistance(pos1: CoordinatesPosition, pos2: CoordinatesPosition) {
    return Math.round(Math.sqrt(Math.pow(pos1.x - pos2.x, 2) + Math.pow(pos1.y - pos2.y, 2)));
  }

  private isInVoiceArea(
    sharerPosition: CoordinatesPosition,
    userPosition: CoordinatesPosition,
    sharerVoiceArea?: number,
  ) {
    const threshold = sharerVoiceArea ?? NO_SOUND_THRESHOLD_DISTANCE;
    const distance = this.calculateDistance(sharerPosition, userPosition);
    return distance < threshold;
  }
}

export interface RoomPosition {
  leftUpperCorner: CoordinatesPosition;
  rightBottomCorner: CoordinatesPosition;
  volume?: number;
}
export type VolumeModes = 'Muted' | 'DistanceSensitive' | 'MaxVolume';

export interface DropdownOption {
  id: string;
  label: string;
}

export type NotificationModes = 'No' | 'All' | 'Custom';
export type CustomModes = CustomMode[];

export interface SettingsSnapshot {
  selectedVideoId: string;
  selectedAudioId: string;
  selectedOutputId: string;
  displayName: string;
  voiceAreaColor: string;
  avatarBase64?: string;
  avatarShape?: 'circle' | 'rectangle';
}
export interface GeneralSettingsSnapshot {
  settings: SettingsSnapshot;
  properties: Partial<UserProperties>;
}
export interface ImageWithDimensions {
  base64: string;
  height: number;
  width: number;
  file?: File;
}
export interface Role {
  id: string;
  name: string;
}
export type AuthMode = '' | 'guest' | 'auth-service';
//----
export interface User {
  /** ユーザーID */
  id: string;
  /** 認証の種類　guest / password / aad  / b2c-demo */
  authType?: string;
  /** ユーザー名　aadと連携時は、フルネーム */
  name: string;
  /** ユーザー表示名 空の場合は ユーザー名を使う */
  displayName?: string;
  /** 声量範囲の色 */
  voiceAreaColor?: string;
  /** 最終ログイン時刻 date.getTime() */
  lastLogin?: number;
  /** アバター画像情報 */
  avatarBase64?: string;
  /** アバター画像を丸く区切るかどうか.省略時は circle */
  avatarShape?: 'circle' | 'rectangle';
  /** aad で認証したときのID.email と違うときがあるので一応 */
  upn?: string;
  /** メールアドレス。ユニークにする必要があります。 */
  email?: string;
  /** パスワードのハッシュ。空の場合は、AAD連携での認証のみ対応 */
  passwordHash?: string;
  /** 会社名. aad での company. AAD 連携時に設定されていた場合はそちらが優先される。 */
  company?: string;
  /** 所属名. aad での department. AAD 連携時に設定されていた場合はそちらが優先される。 */
  department1?: string;
  /** 所属名2  */
  department2?: string;
  /** 役職  */
  jobTitle?: string;
  /** ロール一覧 */
  roles?: Role[];
  /** 参照リスト */
  myList: User[];
  /** 削除フラグ */
  isDeleted?: boolean;
  /** 認証トークン */
  tokenHash: string;
  /**
   * デモサイト用AzureB2CにおけるユーザーのオブジェクトID。（削除時に使うことを想定）
   */
  b2cObjectId?: string;
}

export interface MessageHistory {
  id: string;
  userId: string;
  message: string;
  count: number;
  isFavorite: boolean;
}

export type CustomMode = 'Mylist' | 'Guest';
export type MenuCategory = 'favorites' | 'all' | 'bySpace';
export interface UserProperties {
  /** ユーザーID */
  userId: string;
  /** 入室時の通知設定 */
  selectedNotificationMode: NotificationModes;
  /** 近くに来た時の通知設定 */
  selectedProximityNotificationMode: NotificationModes;
  /** 近くでメッセージを出した時の通知設定 */
  selectedMessageNotificationMode: NotificationModes;
  /** 入室時(接近時、接近者のメッセージ出た時)の通知設定カスタム設定の時の詳細 今は必ず MyList のみ */
  selectedCustomModes: CustomMode[];
  // /** 入室時にビデオをonにして入るかどうか。デフォルトは off */
  // videoEnableOnInit: boolean;
  // /** 入室時に音声をonにして入るかどうか。デフォルトは off */
  // audioEnableOnInit: boolean;
  /** ノイズキャンセルを利用するかどうか。デフォルトは on */
  enableNoiseSuppressor: boolean;
  /** ショートカット機能を利用するかどうか。デフォルトは off */
  enableShortcut: boolean;

  /** 最後に選択してたメニューカテゴリ。お気に入り(favorites) or すべて(all) or スペース(bySpace) のいずれか */
  lastSelectedMenuCategory: MenuCategory;
  /** 最後に入室したスペースID */
  lastSelectedSpaceId: string;
  /** 最後に入室したルームID */
  lastSelectedRoomId: string;
  /** 最後に選択したルーム情報タブ */
  lastSelectedRoomInfoTab: string;
  /** 最後に選択したルーム履歴フィルター */
  lastSelectedRoomHistoryFilters: string[];
  /**
   * デモアンケートを回答したか
   */
  demoQuestionnarieAnswered: boolean;
  /** トライアル版: ユーザー自動削除フラグ */
  autoDelete?: boolean;
}

export interface ScheduleItem {
  isPrivate: boolean;
  status: 'busy' | 'free' | 'tentative' | 'oof' | 'workingElsewhere' | string;
  subject: string;
  location: string;
  isMeeting: boolean;
  isRecurring: boolean;
  isException: boolean;
  isReminderSet: boolean;
  isAllDay?: boolean;
  isCancelled?: boolean;
  start: { dateTime: Date | string; timeZone: string };
  end: { dateTime: Date | string; timeZone: string };
}
export interface WorkingHour {
  daysOfWeek: string[];
  startTime: string;
  endTime: string;
  timeZone: { name: string };
}
/// ms graph api response
export interface ScheduleInfo {
  /**
   * ユーザーID
   * "scheduleId": "kojim@7659sw.com",
   */
  scheduleId: string;
  /**
   * これなんだろう？
   */
  availabilityView: string;
  scheduleItems: ScheduleItem[];
  workingHours: WorkingHour;
}
export interface GetScheduleResponse {
  value: ScheduleInfo[];
}

export interface LoginInfo {
  authMode: AuthMode;
  /**
   * ゲストのユーザー名。guest 以外は無視されます。
   */
  name?: string;
  version?: string;
  versionText?: string;
  id: string;
  tokenHash: string;
  providerId?: string;
  loginWithMagicLink?: boolean;
}

export type UploadFileType = 'image' | 'audio' | 'map-image' | 'room';
export interface UploadFileRequest {
  id: string;
  userId: string;
  fileType: UploadFileType;
}

export interface UsersWithRights {
  users: User[];
  rooms: Room[];
  siteMembers: SiteMember[];
  loginUserIds: string[];
  roomStates: UserRoomState[];
}

export interface LoginProvider {
  id: string;
  authType: string;
  name: string;
  canRegister: boolean;
  enable: boolean;
  sortOrder: number;
  isB2cTenant: boolean;
  allowAccountCreationInAdvance?: boolean;
}

export interface RoomCount {
  [roomId: string]: number;
}

export interface QuestionnaireQuestion {
  id: string;
  question: string;
  optionType: 'radio' | 'select' | 'checkbox';
  options: QuestionnaireOption[];
}

export interface QuestionnaireOption {
  id: string;
  questionId: string;
  value: string;
  text: string;
}
export interface QuestionnarieResult {
  id: string;
  results: {
    questionId: string;
    options: QuestionnaireOption[];
  }[];
}
export interface Campaign {
  id: string;
  title: string;
  body: string;
  url: string;
  visible: boolean;
}

/**
 * ログファイルのリクエスト情報
 */
export interface LogRequest {
  from?: Date;
  to?: Date;
}

/**
 * メンバーの作成情報。
 */
export interface MembersRegistration {
  members: (SiteMember & { password?: string })[];
  /**
   * trueの時にzawaroomアカウントも同時に作成されます。
   */
  createZawaroomAccount: boolean;
  providerId: string;
}
/**
 * メンバーの作成失敗結果
 */
export interface MemberRegistrationFailiureResult {
  email: string;
  reason: string;
}
/**
 * ゲスト利用リクエスト情報
 */
export interface GuestUserRequest {
  email: string;
}
/**
 * 招待されたスペースIDとルームID
 */
export type InvitedSpaceIdAndRoomId = [string, string];
